import { ReactNode } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { AnalyticsHooks, AnalyticsStyles, DemoChip } from '@frontend/analytics-shared';
import { AreaChartData, Chart, TooltipInterfaceProps } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { trackingIds } from '../../../../tracking-ids';
import { CompareLocationsButton } from '../../../compare-locations-button';
import DashboardNSRChartDisclaimer from '../../../dashboard-nsr-chart-disclaimer';

const colors = {
  confirmed: theme.colors.success30,
  existingPatients: theme.colors.primary30,
  newPatients: theme.colors.warning50,
  practice: theme.colors.secondary.seaweed40,
  unconfirmed: theme.colors.rose40,
};

const labels = {
  confirmed: i18next.t('Confirmed Appointments', { ns: 'analytics' }),
  existingPatients: i18next.t('Existing Patients', { ns: 'analytics' }),
  newPatients: i18next.t('New Patients', { ns: 'analytics' }),
  practice: i18next.t('No-Show Rate', { ns: 'analytics' }),
  unconfirmed: i18next.t('Unconfirmed Appointments', { ns: 'analytics' }),
};

interface Props {
  afterChart?: ReactNode;
  beforeChart?: ReactNode;
  bottomContentRenderer?: ReactNode;
  chartData: AreaChartData;
  infoTip: ReactNode;
  isLoading?: boolean;
  isOnDashboard?: boolean;
  legendValues?: Record<string, number | string>;
  onShowMoreClick?: () => void;
  showCompareMulti?: boolean;
  showLegends?: boolean;
  title: string;
  tooltipBottomContentRenderer?: TooltipInterfaceProps['bottomContentRenderer'];
  trackingIdBase: string;
}

export const NSRRoiTimelineChartBase = ({
  afterChart,
  beforeChart,
  bottomContentRenderer,
  chartData,
  infoTip,
  isLoading,
  isOnDashboard,
  legendValues,
  onShowMoreClick,
  showCompareMulti,
  showLegends,
  title,
  tooltipBottomContentRenderer,
  trackingIdBase,
}: Props) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const { noLocationsHaveAclAccess } = AnalyticsHooks.useHasAclAccess({
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });
  const navigate = useNavigate();

  return (
    <Chart
      bottomContentRenderer={bottomContentRenderer}
      colors={colors}
      css={isOnDashboard ? AnalyticsStyles.dashboardChartStyle : undefined}
      isLoading={isLoading}
      labels={labels}
      onClick={onShowMoreClick}
      style={isOnDashboard ? {} : { marginBottom: 0 }}
      trackingId={`${trackingIdBase}-chart`}
    >
      <Chart.Header
        actions={
          isOnDashboard
            ? [
                {
                  label: t('Show More'),
                  omit: noLocationsHaveAclAccess,
                  onClick: () => {
                    navigate({
                      to: '/analytics/roi/no-show-rate',
                    });
                  },
                  trackingId: trackingIds.analytics.showMoreNoShowRateRoi,
                },
              ]
            : []
        }
        title={title}
        infoTip={isOnDashboard ? undefined : infoTip}
        bottomElement={
          <>
            {showCompareMulti && (
              <CompareLocationsButton onClick={onShowMoreClick} trackingId={`${trackingIdBase}-compare-locations`} />
            )}
            {isOnDashboard && <DashboardNSRChartDisclaimer />}
          </>
        }
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin={!showCompareMulti}
      />
      {beforeChart}
      <Chart.AreaChart
        appearance={{
          customTooltipTitle: ({ groupName }) => {
            return (
              <Chip leftElement={<Icon name='calendar-small' />} variant='neutral'>
                {groupName}
              </Chip>
            );
          },
          customYAxisTickFormat: formatters.appendPercent,
          height: 200,
          margin: { top: 30 },
          showXAxis: true,
          showYAxis: true,
          tooltipBottomContentRenderer,
          xAxisTickWidth: 100,
        }}
        data={chartData}
        formatValue={formatters.appendPercent}
      />
      {showLegends && <Chart.Legends values={legendValues} />}
      {afterChart}
    </Chart>
  );
};
